/*global Autodesk*/
import { LmViewer3D } from "@/types/LmViewer3D/LmViewer3D";
import { FeatureManagementType, Token } from "@api/apiClient";
import { useViewToken } from "@api/auth";
import useAppStore from "@hooks/useAppStore";
import useLmViewer3D from "@hooks/useLmViewer3D";
import useScopeStore from "@hooks/useScopeStore";
import useSelectionManager from "@hooks/useSelectionManager";
import { QueryObserverResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";
//import { Viewing } from "@types/LmViewer3D/Extensions/Viewing.extensions";
import { Viewing } from "@/types/LmViewer3D/Extensions/Viewing.Extension.Events";
import { ProjectAnalysisTabs } from "@/types/projectAnalysisTab";
import { ViewerIds } from "@/types/SelectionManager";
import { useAppSettings } from "@api/appSettings";
import { useScenarios } from "@api/scenarios";
import useProjectAnalysisTabs from "@features/analysis/hooks/useProjectAnalysisTabs";
import waitForElm from "@utils/waitForElem";
import { useParams } from "react-router-dom";

// Default extensions loaded by all tabs.
// Other extensions are loaded dynamically depending on the tab (see below)
const defaultExtensions: string[] = [
  //'Viewing.Extension.TotalCO2'
  "Viewing.Extension.TextOverlay",
  "Autodesk.VisualClusters",
  //"Autodesk.ModelAlignment",
  //"Autodesk.ModelsPanel",
];

export interface UseViewersProps {
  activeTab: number; // TODO: should not know about active tab. Parent should handle this.
  enableViewer1: boolean;
  enableViewer2: boolean;
  showViewer2: boolean;
}

const useLmViewers = (props: UseViewersProps) => {
  console.log("useLmViewers", props);
  const { id: projectId } = useParams();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const scopeStore = useScopeStore();
  const appSettings = useAppSettings();
  const [loadViewer1, setLoadViewer1] = useState(false);
  const [loadViewer2, setLoadViewer2] = useState(false);
  const scenarios = useScenarios(projectId!);
  const lmViewer3D = useLmViewer3D();
  const viewToken = useViewToken();
  const projectAnalysisTabs = useProjectAnalysisTabs();

  useEffect(() => {
    appStore.viewerToken.setToken(viewToken.data);
  }, [viewToken.data]);

  useEffect(() => {
    if (appStore.hasFeature(FeatureManagementType.ModelScopes)) {
      if (!defaultExtensions.includes("Viewing.Extension.Scopes")) {
        defaultExtensions.push("Viewing.Extension.Scopes");
      }
    }
  }, [appStore.settings]);

  useEffect(() => {
    setLoadViewer1(props.enableViewer1);
  }, [props.enableViewer1]);

  useEffect(() => {
    setLoadViewer2(props.enableViewer2);
  }, [props.enableViewer2, props.showViewer2]);

  useEffect(() => {
    console.log("useLmViewers: ", loadViewer1, appStore.lmViewer1);
    if (lmViewer3D.isLoaded && loadViewer1 && appStore.isLmViewerEnabled) {
      if (!appStore.lmViewer1) {
        waitForElm('#lmViewer1').then(() => {
          const lmViewer = new LmViewer3D("lmViewer1", {
            language: appStore.language,
            getAccessToken: function (onTokenReady: (token: string, timeInSeconds: number) => void) {
              viewToken.refetch().then((value: QueryObserverResult<Token, unknown>) => {
                if (value.data) {
                  onTokenReady(value.data.accessToken!, value.data.expiresIn!);
                }
              });
            },
          });
          appStore.setLMViewer1(lmViewer);

          // In this case, we are loading the viewer BEFORE model selection
          // so there is no need to call SelectionManager.onLoad after initialization
          lmViewer.initialize(defaultExtensions, () => {
            appStore.viewer1?.setTheme(appStore.colorMode + "-theme");

            // Dynamically load extensions depending on the active tab
            if (projectAnalysisTabs.tabs.at(props.activeTab) === ProjectAnalysisTabs.Results) {
              const ext1 = appStore.viewer1?.getExtension("Viewing.Extension.TotalCO2");
              if (!ext1) {
                appStore.viewer1?.loadExtension("Viewing.Extension.TotalCO2", {
                  viewerIndex: 0,
                  emitter: appStore.lmViewer1?.emitter
                });
              }
            }

            (async () => {
              await selectionManager.refreshViews();
            })();
          });
        });
      }
      setLoadViewer1(false);
    }
  }, [lmViewer3D.isLoaded, loadViewer1, appStore.isLmViewerEnabled]);

  useEffect(() => {
    console.log("useLmViewers: ", loadViewer2, appStore.lmViewer2);
    if (lmViewer3D.isLoaded && loadViewer2 && props.showViewer2 && appStore.isLmViewerEnabled) {
      if (!appStore.lmViewer2) {
        waitForElm('#lmViewer2').then(() => {
          const lmViewer = new LmViewer3D("lmViewer2", {
            language: appStore.language,
            getAccessToken: function (onTokenReady: (token: string, timeInSeconds: number) => void) {
              viewToken.refetch().then((value: QueryObserverResult<Token, unknown>) => {
                if (value.data) {
                  onTokenReady(value.data.accessToken!, value.data.expiresIn!);
                }
              });
            },
          });
          appStore.setLMViewer2(lmViewer);

          // In this case, we are loading the viewer AFTER model selection
          // so we need to call appStore.onLoad when initialization is complete
          lmViewer.initialize(defaultExtensions, () => {
            appStore.viewer2?.setTheme(appStore.colorMode + "-theme");

            // Dynamically load extensions depending on the active tab
            if (props.activeTab === ProjectAnalysisTabs.Results) {
              const ext2 = appStore.viewer2?.getExtension("Viewing.Extension.TotalCO2");
              if (!ext2) {
                appStore.viewer2?.loadExtension("Viewing.Extension.TotalCO2", {
                  viewerIndex: 1,
                  emitter: appStore.lmViewer2?.emitter
                });
              }
            }

            (async () => {
              await selectionManager.refreshViews();
            })();
          });
          appStore.viewer1?.resize();
        });
      }
      setLoadViewer2(false);
    } else {
      if (!props.enableViewer2 && appStore.lmViewer2) {
        appStore.setLMViewer2(null);
      } else {
        appStore.viewer2?.resize();
      }
      if (!props.showViewer2) {
        appStore.viewer1?.resize();
      }
    }
  }, [lmViewer3D.isLoaded, props.showViewer2, loadViewer2, appStore.isLmViewerEnabled]);

  useEffect(() => {
    const handleScopesInitialized = async () => {
      console.log("SCOPES_INITIALIZED");
      scopeStore.setIsScopesEnabled(true);
    };
    const handleScopesUninitialized = async () => {
      console.log("SCOPES_UNINITIALIZED");
      scopeStore.setIsScopesEnabled(false);
    };
    const handleScopesSelectionChanged = async (event: any) => {
      console.log("SCOPES_SELECTION_CHANGED_EVENT: ", event);
      scopeStore.setSelectedScopeIds(event.scopeIds);

      // TODO: add this to lmViewer2
      if (selectionManager.selectedModel?.modelId) {
        const scopes = scopeStore.modelScopes[selectionManager.selectedModel?.modelId]?.scopes;
        if (scopes) {
          let cadIds: string[] = [];
          Object.entries(scopes).forEach(([key, value]) => {
            if (event.scopeIds.includes(+key)) {
              cadIds = cadIds.concat(value);
            }
          });

          (async () => {
            await appStore.lmViewer1?.isolateByCadIds(cadIds, selectionManager.selectedModel?.apsViewId);
          })();
        }
      }
    };
    const handleTextOverlayLegend = (_event: any) => {
      const scenarioId = selectionManager.getScenarioIdByViewerId(ViewerIds.Viewer1);
      if (scenarioId) {
        const scenario = scenarios.data?.find((s) => s.scenarioId === scenarioId);
        appStore.lmViewer1?.emitter.emit(Viewing.Extensions.TEXTOVERLAY_UPDATE_LEGEND_EVENT, scenario?.name);
      }
    };
    const handleTextOverlayLegendReset = (_event: any) => {
      appStore.lmViewer1?.emitter.emit(Viewing.Extensions.TEXTOVERLAY_UPDATE_LEGEND_EVENT, '');
    };

    if (appStore.lmViewer1) {
      console.log("useLmViewers: add lmViewer1 event listeners");
      appStore.lmViewer1?.emitter.on(Viewing.Extensions.SCOPES_INITIALIZED_EVENT, handleScopesInitialized);
      appStore.lmViewer1?.emitter.on(Viewing.Extensions.SCOPES_UNINITIALIZED_EVENT, handleScopesUninitialized);
      appStore.lmViewer1?.emitter.on(Viewing.Extensions.SCOPES_SELECTION_CHANGED_EVENT, handleScopesSelectionChanged);
      appStore.lmViewer1?.emitter.on(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleTextOverlayLegend);
      appStore.lmViewer1?.emitter.on(Autodesk.Viewing.UNLOAD_ALL_DOCUMENTS_EVENT, handleTextOverlayLegendReset);
    }
    return () => {
      if (appStore.lmViewer1) {
        console.log("useLmViewers: remove lmViewer1 event listeners");
        appStore.lmViewer1.emitter.off(Viewing.Extensions.SCOPES_INITIALIZED_EVENT, handleScopesInitialized);
        appStore.lmViewer1.emitter.off(Viewing.Extensions.SCOPES_UNINITIALIZED_EVENT, handleScopesUninitialized);
        appStore.lmViewer1.emitter.off(Viewing.Extensions.SCOPES_SELECTION_CHANGED_EVENT, handleScopesSelectionChanged);
        appStore.lmViewer1.emitter.off(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleTextOverlayLegend);
        appStore.lmViewer1.emitter.off(Autodesk.Viewing.UNLOAD_ALL_DOCUMENTS_EVENT, handleTextOverlayLegendReset);
      }
    };
  }, [appStore.lmViewer1]);

  useEffect(() => {
    const handleScopesInitialized = async () => {
      console.log("SCOPES_INITIALIZED");
      scopeStore.setIsScopesEnabled(true);
    };
    const handleScopesUninitialized = async () => {
      console.log("SCOPES_UNINITIALIZED");
      scopeStore.setIsScopesEnabled(false);
    };
    const handleScopesSelectionChanged = async (event: any) => {
      console.log("SCOPES_SELECTION_CHANGED_EVENT: ", event);
      scopeStore.setSelectedScopeIds(event.scopeIds);
    };
    const handleTextOverlayLegend = (_event: any) => {
      const scenarioId = selectionManager.getScenarioIdByViewerId(ViewerIds.Viewer2);
      if (scenarioId) {
        const scenario = scenarios.data?.find((s) => s.scenarioId === scenarioId);
        appStore.lmViewer2?.emitter.emit(Viewing.Extensions.TEXTOVERLAY_UPDATE_LEGEND_EVENT, scenario?.name);
      }
    };
    const handleTextOverlayLegendReset = (_event: any) => {
      appStore.lmViewer2?.emitter.emit(Viewing.Extensions.TEXTOVERLAY_UPDATE_LEGEND_EVENT, '');
    };

    if (appStore.lmViewer2) {
      console.log("useLmViewers: add lmViewer2 event listeners");
      appStore.lmViewer2?.emitter.on(Viewing.Extensions.SCOPES_INITIALIZED_EVENT, handleScopesInitialized);
      appStore.lmViewer2?.emitter.on(Viewing.Extensions.SCOPES_UNINITIALIZED_EVENT, handleScopesUninitialized);
      appStore.lmViewer2?.emitter.on(Viewing.Extensions.SCOPES_SELECTION_CHANGED_EVENT, handleScopesSelectionChanged);
      appStore.lmViewer2?.emitter.on(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleTextOverlayLegend);
      appStore.lmViewer2?.emitter.on(Autodesk.Viewing.UNLOAD_ALL_DOCUMENTS_EVENT, handleTextOverlayLegendReset);
    }
    return () => {
      if (appStore.lmViewer2) {
        console.log("useLmViewers: remove lmViewer2 event listeners");
        appStore.lmViewer2.emitter.off(Viewing.Extensions.SCOPES_INITIALIZED_EVENT, handleScopesInitialized);
        appStore.lmViewer2.emitter.off(Viewing.Extensions.SCOPES_UNINITIALIZED_EVENT, handleScopesUninitialized);
        appStore.lmViewer2.emitter.off(Viewing.Extensions.SCOPES_SELECTION_CHANGED_EVENT, handleScopesSelectionChanged);
        appStore.lmViewer2.emitter.off(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, handleTextOverlayLegend);
        appStore.lmViewer2.emitter.off(Autodesk.Viewing.UNLOAD_ALL_DOCUMENTS_EVENT, handleTextOverlayLegendReset);
      }
    };
  }, [appStore.lmViewer2]);

  // Enable/Disable Viewing.Extension.TotalCO2 based on tab selection
  useEffect(() => {
    if (props.activeTab === ProjectAnalysisTabs.Results) {
      const ext1 = appStore.viewer1?.getExtension("Viewing.Extension.TotalCO2");
      if (!ext1) {
        appStore.viewer1?.loadExtension("Viewing.Extension.TotalCO2", {
          viewerIndex: 0,
          emitter: appStore.lmViewer1?.emitter
        });
      }
      const ext2 = appStore.viewer2?.getExtension("Viewing.Extension.TotalCO2");
      if (!ext2) {
        appStore.viewer2?.loadExtension("Viewing.Extension.TotalCO2", {
          viewerIndex: 1,
          emitter: appStore.lmViewer2?.emitter
        });
      }
    } else {
      appStore.viewer1?.unloadExtension("Viewing.Extension.TotalCO2");
      appStore.viewer2?.unloadExtension("Viewing.Extension.TotalCO2");
    }
  }, [props.activeTab]);

  useEffect(() => {
    appStore.viewer1?.setTheme(appStore.colorMode + "-theme");
    appStore.viewer2?.setTheme(appStore.colorMode + "-theme");
  }, [appStore.colorMode]);

  useEffect(() => {
    return () => {
      console.log("useLmViewers: cleanup");
      appStore.setLMViewer1(null);
      appStore.setLMViewer2(null);
    };
  }, []);
};

export default useLmViewers;